'use client';

import cx from 'classix';
import { setCookie } from 'cookies-next/client';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import {
  Button as AriaButton,
  DialogTrigger,
} from 'react-aria-components';

import ListingMessageModal from '@/features/listing/components/listing-message-modal/listing-message-modal';
import {
  type ListingDataView,
  type ListingWithUser,
} from '@/features/listing/types/listing-types';
import useAppFlyerLink from '@/features/roomster-app/hooks/use-app-flyer-link';
import { loggedUserAtom } from '@/features/user/atoms/logged-user-atom';
import { useRouter } from '@/i18n/routing';
import { ROUTER_LINKS } from '@/shared/constants/links-constants';
import { SECONDS } from '@/shared/constants/time-constants';
import { type MessageSentSource } from '@/shared/types/report-types';
import Button from '@/ui/button/button';
import buttonStyles from '@/ui/button/button.module.scss';
import Modal from '@/ui/modal/modal';

interface ListingMessageProps {
  listing: ListingWithUser;
  view?: ListingDataView;
  messageSrc?: MessageSentSource,
}

export default function ListingMessageButton(props: ListingMessageProps) {
  const {
    listing,
    messageSrc,
    view = 'isCard',
  } = props;

  const translationsListingCard = useTranslations('listing.card');
  const router = useRouter();

  const loggedUser = useAtomValue(loggedUserAtom);

  const appFlyerLinkName = view === 'isCard' ? 'mobileMessageButton' : 'mobileListingMessageButton';
  const appFlyerLink = useAppFlyerLink('mobile', appFlyerLinkName, ROUTER_LINKS.phoneAuth);

  const [isModalActive,
    setIsModalActive] = useState(false);

  const handleOnMessageClick = () => {
    // TODO: [auth-redirect]

    if (!loggedUser) {
      router.push(ROUTER_LINKS.phoneAuth);
      return;
    }

    if (!loggedUser.verified) {
      router.push(ROUTER_LINKS.accountValidation);
      return;
    }

    setIsModalActive(true);
  };

  const closeMessageModal = () => {
    setIsModalActive(false);
  };

  const handleMessageAuthData = () => {
    setCookie('authData', JSON.stringify({ token: 'send-message', userName: listing.user.firstName }), { maxAge: SECONDS.HOUR, path: '/' });
  };

  return loggedUser ? (
    // eslint-disable-next-line canonical/prefer-react-lazy
    <DialogTrigger>
      <AriaButton
        className={cx(
          buttonStyles.base,
          buttonStyles.size_md,
          buttonStyles.variant_filled,
          buttonStyles.shade_dark,
          buttonStyles.size_full,
          buttonStyles.font_xs,
        )}
        onPressUp={handleOnMessageClick}
      >
        {translationsListingCard('message')}
      </AriaButton>

      <Modal isOpen={isModalActive} onOpenChange={setIsModalActive} isCloseOnClickOutside showCloseIcon>
        <ListingMessageModal listing={listing} onClose={closeMessageModal} messageSrc={messageSrc} />
      </Modal>
    </DialogTrigger>
  ) : (
    // eslint-disable-next-line canonical/prefer-react-lazy
    <Button
      element="link"
      onClick={handleMessageAuthData}
      href={appFlyerLink}
      passHref
      size="md"
      fontSize="xs"
      fullWidth
    >
      {translationsListingCard('message')}
    </Button>
  );
}
